/* ThaBel — núcleo do cálculo de frete (usado pela página do produto e pelo carrinho)
   - Estado: descoberto pela faixa do CEP, no próprio tema; BrasilAPI e ViaCEP só de reserva, se o CEP estiver fora das faixas
   - Frenet: uma chamada direta à Shopify (cart/shipping_rates.json); se falhar, prepara e confere a cada 0,3 s, até duas vezes
   - Cache: guarda o resultado por 10 min (mesmo CEP + mesmo conteúdo) */
(() => {
  if (window.TbFrete) return;

  const CACHE_KEY = 'tb-frete-cache';
  const CACHE_MS = 10 * 60 * 1000;
  const espera = (ms) => new Promise((ok) => setTimeout(ok, ms));

  function comTempo(url, opcoes = {}, ms = 8000) {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), ms);
    return fetch(url, { ...opcoes, signal: ctrl.signal }).finally(() => clearTimeout(t));
  }

  function lerCache() {
    try { return JSON.parse(sessionStorage.getItem(CACHE_KEY) || '{}') || {}; } catch (e) { return {}; }
  }
  function cacheObter(chave) {
    const c = lerCache()[chave];
    if (!c || Date.now() - c.t > CACHE_MS) return null;
    return c;
  }
  function cacheGuardar(chave, dados) {
    try {
      const tudo = lerCache();
      const agora = Date.now();
      Object.keys(tudo).forEach((k) => { if (agora - tudo[k].t > CACHE_MS) delete tudo[k]; });
      tudo[chave] = { ...dados, t: agora };
      sessionStorage.setItem(CACHE_KEY, JSON.stringify(tudo));
    } catch (e) { /* sem armazenamento: tudo bem */ }
  }

  /* Faixas oficiais de CEP por estado (5 primeiros dígitos). */
  const FAIXAS = [
    [1000, 19999, 'SP'], [20000, 28999, 'RJ'], [29000, 29999, 'ES'], [30000, 39999, 'MG'],
    [40000, 48999, 'BA'], [49000, 49999, 'SE'], [50000, 56999, 'PE'], [57000, 57999, 'AL'],
    [58000, 58999, 'PB'], [59000, 59999, 'RN'], [60000, 63999, 'CE'], [64000, 64999, 'PI'],
    [65000, 65999, 'MA'], [66000, 68899, 'PA'], [68900, 68999, 'AP'], [69000, 69299, 'AM'],
    [69300, 69399, 'RR'], [69400, 69899, 'AM'], [69900, 69999, 'AC'], [70000, 72799, 'DF'],
    [72800, 72999, 'GO'], [73000, 73699, 'DF'], [73700, 76799, 'GO'], [76800, 76999, 'RO'],
    [77000, 77999, 'TO'], [78000, 78899, 'MT'], [79000, 79999, 'MS'], [80000, 87999, 'PR'],
    [88000, 89999, 'SC'], [90000, 99999, 'RS'],
  ];
  /* Devolve a sigla do estado pela tabela, ou null se o CEP estiver fora de todas as faixas. */
  function estadoDoCep(cep) {
    const n = parseInt(String(cep).replace(/\D/g, '').slice(0, 5), 10);
    const f = FAIXAS.find(([ini, fim]) => n >= ini && n <= fim);
    return f ? f[2] : null;
  }

  /* Reserva: só é usada se o CEP não estiver na tabela (ex.: faixa nova criada pelos Correios).
     Consulta BrasilAPI e ViaCEP ao mesmo tempo e usa a primeira que devolver o estado. */
  function estadoPelasApis(cep8) {
    const fontes = [
      comTempo(`https://brasilapi.com.br/api/cep/v1/${cep8}`, {}, 6000)
        .then((r) => (r.ok ? r.json() : Promise.reject(new Error('api'))))
        .then((d) => (d && d.state ? String(d.state).toUpperCase() : Promise.reject(new Error('api')))),
      comTempo(`https://viacep.com.br/ws/${cep8}/json/`, {}, 6000)
        .then((r) => (r.ok ? r.json() : Promise.reject(new Error('api'))))
        .then((d) => (d && !d.erro && d.uf ? String(d.uf).toUpperCase() : Promise.reject(new Error('api')))),
    ];
    return Promise.any(fontes).catch(() => { throw new Error('cep'); });
  }

  /* Tabela primeiro (instantâneo); APIs só de reserva. Lança 'cep' se ninguém reconhecer o CEP. */
  async function descobrirEstado(cep) {
    const cep8 = String(cep).replace(/\D/g, '');
    return estadoDoCep(cep8) || estadoPelasApis(cep8);
  }

  /* Caminho direto: uma única chamada que já espera a Frenet e devolve as taxas prontas.
     Devolve null se a Shopify não responder a tempo ou vier sem lista (aí usamos a reserva). */
  async function taxasDiretas(root, params) {
    try {
      const r = await comTempo(`${root}cart/shipping_rates.json?${params}`, { headers: { Accept: 'application/json' } }, 12000);
      if (r.status === 422) throw new Error('cep');
      if (!r.ok) return null;
      const d = (await r.json().catch(() => null)) || {};
      return Array.isArray(d.shipping_rates) && d.shipping_rates.length ? d.shipping_rates : null;
    } catch (e) {
      if (e && e.message === 'cep') throw e;
      return null;
    }
  }

  /* Reserva: pede à Shopify para preparar e confere até ficar pronto. Devolve a lista ou null se estourar o tempo. */
  async function pedirTaxas(root, params) {
    const prep = await comTempo(`${root}cart/prepare_shipping_rates.json?${params}`, { method: 'POST' }, 20000);
    if (prep.status === 422) throw new Error('cep');
    const inicio = Date.now();
    let pausa = 300;
    while (Date.now() - inicio < 25000) {
      await espera(pausa);
      const r = await comTempo(`${root}cart/async_shipping_rates.json?${params}`, {}, 12000);
      if (r.status === 422) throw new Error('cep');
      /* Enquanto a Frenet não responde, a Shopify devolve o texto "null" — trata como "ainda não", não como erro. */
      const d = (await r.json().catch(() => null)) || {};
      if (Array.isArray(d.shipping_rates)) return d.shipping_rates;
      pausa = Math.min(pausa + 200, 1000);
    }
    return null;
  }

  /* 1º o caminho direto; se falhar, a reserva (até duas vezes). Avisa "só mais um instante" se passar de 5 s. */
  async function taxas(root, cepFmt, aoDemorar, ufPronta) {
    const uf = ufPronta || await descobrirEstado(cepFmt);
    const params = new URLSearchParams({
      'shipping_address[zip]': cepFmt,
      'shipping_address[country]': 'Brazil',
      'shipping_address[province]': uf,
    }).toString();
    const aviso = aoDemorar ? setTimeout(aoDemorar, 5000) : null;
    try {
      const direta = await taxasDiretas(root, params);
      if (direta) return direta;
      let ultimoErro = null;
      for (let tentativa = 0; tentativa < 2; tentativa += 1) {
        try {
          const lista = await pedirTaxas(root, params);
          if (lista) return lista;
          ultimoErro = new Error('tempo');
        } catch (e) {
          if (e && e.message === 'cep') throw e;
          ultimoErro = e;
        }
        await espera(400);
      }
      throw ultimoErro || new Error('tempo');
    } finally {
      if (aviso) clearTimeout(aviso);
    }
  }

  const moeda = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
  function htmlLista(lista) {
    const itens = lista.map((t) => {
      const preco = Number(t.price);
      const d = Array.isArray(t.delivery_days) ? t.delivery_days : [];
      const dias = d.length ? (d.length === 1 || d[0] === d[1] ? `Entrega em até ${d[d.length - 1]} dias úteis` : `Entrega de ${d[0]} a ${d[1]} dias úteis`) : '';
      const nome = String(t.name).replace(/[<>&"]/g, '');
      return `<li class="tb-cep__opcao"><span><strong>${nome}</strong>${dias ? `<span class="tb-suave">${dias}</span>` : ''}</span><span class="${preco === 0 ? 'tb-cep__gratis' : ''}">${preco === 0 ? 'Grátis' : moeda.format(preco)}</span></li>`;
    }).join('');
    return `<ul class="tb-cep__lista" role="list">${itens}</ul>`;
  }

  const MSG_CEP = '<p class="tb-cep__erro">CEP não encontrado. Confira os números.</p>';
  function mensagemErro(erro) {
    if (erro && erro.message === 'cep') return MSG_CEP;
    return '<p class="tb-cep__erro">Não foi possível calcular agora. <button type="button" class="tb-link-botao" data-tb-cep-tentar>Tentar de novo</button></p>';
  }

  window.TbFrete = { estadoDoCep, descobrirEstado, taxas, cacheObter, cacheGuardar, htmlLista, mensagemErro, MSG_CEP, comTempo, moeda };
})();

/* ThaBel — página de produto */
(() => {
  if (customElements.get('tb-produto')) return;

  class TbProduto extends HTMLElement {
    connectedCallback() {
      const dados = this.querySelector('[data-tb-product]');
      if (!dados) return;
      this.product = JSON.parse(dados.textContent);
      this.cfg = {
        pix: Number(this.dataset.pix) || 0,
        parcMax: Math.max(1, Number(this.dataset.parcelas) || 1),
        parcMin: Number(this.dataset.parcelaMin) || 0,
        cashback: Number(this.dataset.cashback) || 0,
      };
      this.moeda = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: this.dataset.currency || 'BRL' });
      this.form = this.querySelector(`#tb-form-${this.dataset.section}`);
      this.idInput = this.form ? this.form.querySelector('input[name="id"]') : null;
      this.cta = this.querySelector('[data-tb-cta]');
      this.fieldsets = [...this.querySelectorAll('[data-tb-opcao]')];
      this.fixas = [...this.querySelectorAll('[data-tb-opcao-fixa]')];
      this.alerta = this.querySelector('[data-tb-alerta]');
      this.variant = null;

      this.fieldsets.forEach((fs) =>
        fs.addEventListener('change', () => {
          this.esconderAlerta();
          fs.classList.remove('is-pedindo');
          this.atualizar(true);
        })
      );

      if (this.form) {
        this.form.addEventListener('submit', (e) => {
          if (!this.variant || !this.variant.available) {
            e.preventDefault();
            this.pedirSelecao();
            return;
          }
          // Com o carrinho lateral ThaBel ativo, adiciona sem sair da página
          const gaveta = document.querySelector('tb-carrinho[data-modo="gaveta"]');
          if (!gaveta || typeof gaveta.adicionarForm !== 'function') return;
          e.preventDefault();
          if (this.adicionando) return;
          this.adicionando = true;
          const cta = this.cta;
          if (cta) { cta.disabled = true; cta.setAttribute('aria-busy', 'true'); cta.textContent = 'Adicionando…'; }
          gaveta
            .adicionarForm(this.form)
            .then(() => this.esconderAlerta())
            .catch((erro) => this.mostrarAlerta(erro.message))
            .finally(() => {
              this.adicionando = false;
              if (cta) cta.removeAttribute('aria-busy');
              this.atualizar(false);
            });
        });
      }

      this.querySelectorAll('[data-tb-qtd]').forEach((btn) =>
        btn.addEventListener('click', () => {
          const input = this.querySelector('input[name="quantity"]');
          if (!input) return;
          input.value = Math.max(1, (parseInt(input.value, 10) || 1) + Number(btn.dataset.tbQtd));
        })
      );

      // Troca de cor: leva o tamanho escolhido para o outro produto
      this.querySelectorAll('[data-tb-cor]').forEach((link) =>
        link.addEventListener('click', () => {
          const tam = this.tamanhoEscolhido();
          if (!tam) return;
          const url = new URL(link.href, window.location.origin);
          url.searchParams.set('tamanho', tam);
          link.href = url.toString();
        })
      );

      // Guia de medidas
      const guia = this.querySelector('[data-tb-guia]');
      if (guia) {
        this.querySelectorAll('[data-tb-abrir-guia]').forEach((b) => b.addEventListener('click', () => guia.showModal()));
        guia.querySelector('[data-tb-fechar-guia]')?.addEventListener('click', () => guia.close());
        guia.addEventListener('click', (e) => { if (e.target === guia) guia.close(); });
      }

      this.iniciarGaleria();
      this.iniciarBarra();

      // Produto sem opções: já pode comprar
      if (this.product.variants.length === 1 && this.fieldsets.length === 0) {
        this.variant = this.product.variants[0];
      }
      this.aplicarTamanhoDaUrl();
      this.atualizar(false);
      if (window.location.search.includes('contact_posted=true')) this.mostrarAviseme(true);
    }

    selecionados() {
      const sel = new Array(this.product.options.length).fill(null);
      this.fixas.forEach((i) => { sel[Number(i.dataset.index)] = i.value; });
      this.fieldsets.forEach((fs) => {
        const marcado = fs.querySelector('input[type="radio"]:checked');
        sel[Number(fs.dataset.index)] = marcado ? marcado.value : null;
      });
      return sel;
    }

    encontrarVariante(sel) {
      if (sel.some((v) => v === null)) return null;
      return this.product.variants.find((v) => v.options.every((o, i) => o === sel[i])) || null;
    }

    tamanhoEscolhido() {
      const fs = this.fieldsets[this.fieldsets.length - 1];
      const marcado = fs ? fs.querySelector('input[type="radio"]:checked') : null;
      return marcado ? marcado.value : null;
    }

    aplicarTamanhoDaUrl() {
      const params = new URLSearchParams(window.location.search);
      const tam = params.get('tamanho');
      if (!tam) return;
      params.delete('tamanho');
      const resto = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (resto ? `?${resto}` : '') + window.location.hash);
      const fs = this.fieldsets[this.fieldsets.length - 1];
      if (!fs) return;
      const input = [...fs.querySelectorAll('input[type="radio"]')].find((i) => i.value === tam);
      if (input) {
        input.checked = true;
        const v = this.encontrarVariante(this.selecionados());
        if (v && v.available) return;
        input.checked = false;
      }
      this.mostrarAlerta(`O tamanho ${tam} não está disponível nesta cor. Escolha outro tamanho.`);
    }

    atualizar(doUsuario) {
      const sel = this.selecionados();
      if (this.fieldsets.length || this.fixas.length) this.variant = this.encontrarVariante(sel);
      const v = this.variant;

      // Marca opções esgotadas considerando o que já foi escolhido
      this.fieldsets.forEach((fs) => {
        const i = Number(fs.dataset.index);
        fs.querySelectorAll('input[type="radio"]').forEach((inp) => {
          const ok = this.product.variants.some(
            (va) => va.available && va.options[i] === inp.value && va.options.every((o, j) => j === i || sel[j] === null || o === sel[j])
          );
          const label = this.querySelector(`label[for="${inp.id}"]`);
          if (label) label.classList.toggle('is-esgotado', !ok);
        });
        const txt = fs.querySelector('[data-tb-opcao-valor]');
        if (txt) txt.textContent = sel[i] || 'selecione';
      });

      if (this.idInput) this.idInput.value = v ? v.id : '';

      if (this.cta) {
        const d = this.cta.dataset;
        if (!v) { this.cta.disabled = true; this.cta.textContent = d.txtSelecione; }
        else if (!v.available) { this.cta.disabled = true; this.cta.textContent = d.txtEsgotado; }
        else { this.cta.disabled = false; this.cta.textContent = d.txtComprar; }
      }

      const esgotado = v && !v.available;
      this.querySelector('[data-tb-estoque-ok]')?.toggleAttribute('hidden', !!esgotado);
      this.querySelector('[data-tb-estoque-fora]')?.toggleAttribute('hidden', !esgotado);
      this.mostrarAviseme(!!esgotado);
      const campoVar = this.querySelector('[data-tb-aviseme-variante]');
      if (campoVar) campoVar.value = v ? v.title : '';

      if (v) {
        this.atualizarPreco(v);
        const sku = this.querySelector('[data-tb-sku]');
        if (sku) { sku.textContent = v.sku || ''; sku.parentElement.toggleAttribute('hidden', !v.sku); }
        if (doUsuario) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', v.id);
          window.history.replaceState({}, '', url.toString());
        }
      }

      const zap = this.querySelector('[data-tb-zap]');
      if (zap) {
        const tam = this.tamanhoEscolhido();
        const msg = `Olá! Tenho uma dúvida sobre: ${zap.dataset.produto}${tam ? ` (tamanho ${tam})` : ''} — ${zap.dataset.url}`;
        zap.href = `https://wa.me/${zap.dataset.zap}?text=${encodeURIComponent(msg)}`;
      }

      this.dispatchEvent(new CustomEvent('tb:variante', { bubbles: true, detail: { variant: v } }));
    }

    atualizarPreco(v) {
      const preco = v.price;
      const pix = Math.floor((preco * (100 - this.cfg.pix)) / 100);
      let n = this.cfg.parcMax;
      if (this.cfg.parcMin > 0) n = Math.min(n, Math.floor(preco / this.cfg.parcMin));
      n = Math.max(1, n);
      const parcela = Math.floor(preco / n);
      const f = (c) => this.moeda.format(c / 100);

      this.querySelectorAll('[data-tb-preco-box]').forEach((box) => {
        box.querySelectorAll('[data-tb-preco]').forEach((el) => { el.textContent = f(preco); });
        box.querySelectorAll('[data-tb-pix]').forEach((el) => { el.textContent = f(pix); });
        box.querySelectorAll('[data-tb-economia]').forEach((el) => { el.textContent = f(preco - pix); });
        box.querySelectorAll('[data-tb-cashback]').forEach((el) => { el.textContent = f(Math.floor((preco * this.cfg.cashback) / 100)); });
        box.querySelectorAll('[data-tb-n]').forEach((el) => { el.textContent = n; });
        box.querySelectorAll('[data-tb-parcela]').forEach((el) => { el.textContent = f(parcela); });
        box.querySelectorAll('[data-tb-parcelamento]').forEach((el) => el.toggleAttribute('hidden', n <= 1));
        box.querySelectorAll('[data-tb-de]').forEach((el) => {
          const tem = v.compare_at_price && v.compare_at_price > preco;
          el.textContent = tem ? f(v.compare_at_price) : '';
          el.toggleAttribute('hidden', !tem);
        });
      });
    }

    mostrarAviseme(mostrar) {
      this.querySelector('[data-tb-aviseme]')?.toggleAttribute('hidden', !mostrar);
    }

    mostrarAlerta(texto) {
      if (!this.alerta) return;
      this.alerta.textContent = texto;
      this.alerta.hidden = false;
    }

    esconderAlerta() {
      if (this.alerta) this.alerta.hidden = true;
    }

    pedirSelecao() {
      const faltando = this.fieldsets.find((fs) => !fs.querySelector('input[type="radio"]:checked')) || this.fieldsets[0];
      if (!faltando) return;
      faltando.classList.add('is-pedindo');
      faltando.scrollIntoView({ behavior: 'smooth', block: 'center' });
      if (!this.variant) this.mostrarAlerta(`Escolha o ${(faltando.dataset.nome || 'tamanho').toLowerCase()} para continuar.`);
    }

    iniciarGaleria() {
      const galeria = this.querySelector('[data-tb-galeria]');
      const thumbs = [...this.querySelectorAll('[data-tb-thumb]')];
      if (!galeria || !thumbs.length) return;
      const itens = [...galeria.children];
      thumbs.forEach((t) =>
        t.addEventListener('click', () => {
          const alvo = itens[Number(t.dataset.tbThumb)];
          if (alvo) galeria.scrollTo({ left: alvo.offsetLeft - galeria.offsetLeft, behavior: 'smooth' });
        })
      );
      if (!('IntersectionObserver' in window)) return;
      const obs = new IntersectionObserver(
        (entradas) => {
          entradas.forEach((e) => {
            if (!e.isIntersecting) return;
            const idx = itens.indexOf(e.target);
            thumbs.forEach((t, i) => t.classList.toggle('is-ativa', i === idx));
          });
        },
        { root: galeria, threshold: 0.6 }
      );
      itens.forEach((i) => obs.observe(i));
    }

    iniciarBarra() {
      const barra = this.querySelector('[data-tb-barra]');
      if (!barra || !this.cta || !('IntersectionObserver' in window)) return;
      const obs = new IntersectionObserver(([e]) => {
        const passou = !e.isIntersecting && e.boundingClientRect.top < 0;
        barra.classList.toggle('is-visivel', passou);
        barra.setAttribute('aria-hidden', passou ? 'false' : 'true');
      });
      obs.observe(this.cta);
      barra.querySelector('[data-tb-barra-botao]')?.addEventListener('click', () => {
        if (this.variant && this.variant.available && this.form) { if (typeof this.form.requestSubmit === 'function') this.form.requestSubmit(); else this.form.querySelector('[type="submit"]')?.click(); }
        else this.pedirSelecao();
      });
    }
  }
  customElements.define('tb-produto', TbProduto);

  class TbRelacionados extends HTMLElement {
    connectedCallback() {
      if (this.dataset.carregado || !this.dataset.url) return;
      this.dataset.carregado = '1';
      fetch(this.dataset.url)
        .then((r) => r.text())
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const novo = doc.querySelector('tb-relacionados');
          if (novo && novo.innerHTML.trim()) this.innerHTML = novo.innerHTML;
        })
        .catch(() => {});
    }
  }
  if (!customElements.get('tb-relacionados')) customElements.define('tb-relacionados', TbRelacionados);

  /* ---------- Frete na página do produto ----------
     A Shopify só calcula frete sobre o carrinho. Então: guarda o carrinho do cliente,
     coloca só este produto, pede as taxas (Frenet) e devolve o carrinho como estava. */
  class TbFreteProduto extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-tb-cep-form]');
      this.campo = this.form.querySelector('input');
      this.saida = this.querySelector('[data-tb-cep-resultado]');
      this.root = this.dataset.root || '/';
      try { const salvo = localStorage.getItem('tb-cep'); if (salvo) this.campo.value = salvo; } catch (e) { /* ok */ }
      this.campo.addEventListener('input', () => {
        const n = this.campo.value.replace(/\D/g, '').slice(0, 8);
        this.campo.value = n.length > 5 ? `${n.slice(0, 5)}-${n.slice(5)}` : n;
      });
      this.form.addEventListener('submit', (e) => { e.preventDefault(); this.calcular(); });
      this.addEventListener('click', (e) => { if (e.target.closest('[data-tb-cep-tentar]')) this.calcular(); });
    }
    varianteAtual() {
      const secao = this.closest('tb-produto') || document;
      const id = secao.querySelector('form[id^="tb-form-"] input[name="id"]')?.value;
      const qtd = parseInt(secao.querySelector('form[id^="tb-form-"] input[name="quantity"]')?.value, 10) || 1;
      return { id, qtd };
    }
    async pedir(url, corpo) {
      const r = await tbBuscar(`${this.root}${url}`, corpo ? { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(corpo) } : { headers: { Accept: 'application/json' } });
      return r;
    }
    /* Restaura o carrinho do cliente. Tenta até 3 vezes; se a página fechar no meio,
       o backup fica em sessionStorage e é restaurado na próxima visita (ver rodapé do arquivo). */
    async restaurar(backup) {
      for (let tentativa = 0; tentativa < 3; tentativa += 1) {
        try {
          const simples = backup.itens.every((i) => !i.properties && !i.selling_plan);
          if (simples) {
            const updates = {};
            backup.tocados.forEach((vid) => { updates[vid] = 0; });
            backup.itens.forEach((i) => { updates[i.id] = i.quantity; });
            const r = await this.pedir('cart/update.js', { updates });
            if (!r.ok) throw new Error('update');
          } else {
            await fetch(`${this.root}cart/clear.js`, { method: 'POST', headers: { Accept: 'application/json' } });
            if (backup.itens.length) {
              const r = await this.pedir('cart/add.js', { items: backup.itens });
              if (!r.ok) throw new Error('add');
            }
          }
          try { sessionStorage.removeItem('tb-frete-backup'); } catch (e) { /* ok */ }
          document.dispatchEvent(new CustomEvent('tb:carrinho-mudou'));
          return true;
        } catch (e) {
          await new Promise((ok) => setTimeout(ok, 800 * (tentativa + 1)));
        }
      }
      console.warn('[ThaBel] frete: não consegui restaurar o carrinho');
      return false;
    }
    async calcular() {
      if (window.__tbFreteOcupado) return;
      const F = window.TbFrete;
      if (!F) { this.saida.innerHTML = '<p class="tb-cep__erro">Não foi possível calcular agora. <button type="button" class="tb-link-botao" data-tb-cep-tentar>Tentar de novo</button></p>'; return; }
      const cep = this.campo.value.replace(/\D/g, '');
      if (cep.length !== 8) { this.saida.innerHTML = '<p class="tb-cep__erro">Digite um CEP com 8 números.</p>'; return; }
      const cepFmt = `${cep.slice(0, 5)}-${cep.slice(5)}`;
      try { localStorage.setItem('tb-cep', cepFmt); } catch (e) { /* ok */ }
      window.__tbFreteOcupado = true;
      let backup = null;
      let mexeu = false;
      try {
        let { id, qtd } = this.varianteAtual();
        if (!id) {
          const p = await tbBuscar(`${window.location.pathname.replace(/\/$/, '')}.js`).then((r) => r.json());
          id = (p.variants.find((v) => v.available) || p.variants[0]).id;
        }
        id = Number(id);
        /* Já calculou este CEP para este produto há pouco? Mostra na hora. */
        const chave = `p:${id}x${qtd}:${cep}`;
        const pronto = F.cacheObter(chave);
        if (pronto) { this.saida.innerHTML = F.htmlLista(pronto.lista); return; }
        this.saida.innerHTML = '<p class="tb-suave">Calculando…</p>';
        const uf = await F.descobrirEstado(cep);

        /* Coloca só este produto no carrinho para a Shopify calcular; devolve tudo no final. */
        const prepararCarrinho = async () => {
          const carrinho = await this.pedir('cart.js').then((r) => r.json());
          const noCarrinho = (carrinho.items || []).slice().reverse().map((i) => {
            const item = { id: i.variant_id, quantity: i.quantity };
            if (i.properties && Object.keys(i.properties).length) item.properties = i.properties;
            if (i.selling_plan_allocation) item.selling_plan = i.selling_plan_allocation.selling_plan.id;
            return item;
          });
          const jaIgual = noCarrinho.length === 1 && noCarrinho[0].id === id && noCarrinho[0].quantity === qtd && !noCarrinho[0].properties;
          if (jaIgual) return;
          backup = { itens: noCarrinho, tocados: [id], t: Date.now() };
          try { sessionStorage.setItem('tb-frete-backup', JSON.stringify(backup)); } catch (e) { /* ok */ }
          mexeu = true;
          const updates = {};
          noCarrinho.forEach((i) => { updates[i.id] = 0; });
          updates[id] = qtd;
          const troca = await this.pedir('cart/update.js', { updates });
          if (!troca.ok) throw new Error('adicionar');
        };
        await prepararCarrinho();

        const lista = await F.taxas(this.root, cepFmt, () => {
          this.saida.innerHTML = '<p class="tb-suave">Consultando as transportadoras, só mais um instante…</p>';
        }, uf);
        if (!lista.length) { this.saida.innerHTML = F.MSG_CEP; return; }
        F.cacheGuardar(chave, { lista });
        this.saida.innerHTML = F.htmlLista(lista);
      } catch (erro) {
        console.warn('[ThaBel] frete no produto:', erro);
        this.saida.innerHTML = F.mensagemErro(erro);
      } finally {
        if (mexeu && backup) await this.restaurar(backup);
        window.__tbFreteOcupado = false;
      }
    }
  }
  /* fetch com limite de tempo (10 s) para não travar o botão se a rede falhar */
  function tbBuscar(url, opcoes = {}, ms = 10000) {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), ms);
    return fetch(url, { ...opcoes, signal: ctrl.signal }).finally(() => clearTimeout(t));
  }
  if (!customElements.get('tb-frete-produto')) customElements.define('tb-frete-produto', TbFreteProduto);
  /* Se um cálculo anterior foi interrompido (fechou a aba no meio), devolve o carrinho do cliente. */
  (async () => {
    let pendente = null;
    try { pendente = JSON.parse(sessionStorage.getItem('tb-frete-backup') || 'null'); } catch (e) { /* ok */ }
    if (!pendente || !Array.isArray(pendente.itens) || Date.now() - pendente.t > 3600000) return;
    const raiz = document.querySelector('tb-frete-produto')?.dataset.root || '/';
    const ctx = { root: raiz, pedir: TbFreteProduto.prototype.pedir };
    await TbFreteProduto.prototype.restaurar.call(ctx, pendente);
  })();

  /* ---------- Cronômetro de oferta ---------- */
  class TbCronometro extends HTMLElement {
    connectedCallback() {
      const m = String(this.dataset.fim || '').match(/(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{1,2}):(\d{2}))?/);
      if (!m) return;
      const [, d, mes, a, h = '23', min = '59'] = m;
      this.alvo = Date.parse(`${a}-${mes.padStart(2, '0')}-${d.padStart(2, '0')}T${h.padStart(2, '0')}:${min}:00-03:00`);
      if (Number.isNaN(this.alvo)) return;
      this.campos = { d: this.querySelector('[data-d]'), h: this.querySelector('[data-h]'), m: this.querySelector('[data-m]'), s: this.querySelector('[data-s]') };
      this.tick();
      this.timer = setInterval(() => this.tick(), 1000);
    }
    disconnectedCallback() { clearInterval(this.timer); }
    tick() {
      const resto = this.alvo - Date.now();
      if (resto <= 0) {
        clearInterval(this.timer);
        if (this.dataset.aoTerminar === 'mensagem') {
          this.hidden = false;
          this.classList.add('is-encerrado');
          this.querySelector('[data-fim-msg]').hidden = false;
        } else {
          this.hidden = true;
        }
        return;
      }
      const t = Math.floor(resto / 1000);
      const dd = (n) => String(n).padStart(2, '0');
      this.campos.d.textContent = dd(Math.floor(t / 86400));
      this.campos.h.textContent = dd(Math.floor((t % 86400) / 3600));
      this.campos.m.textContent = dd(Math.floor((t % 3600) / 60));
      this.campos.s.textContent = dd(t % 60);
      this.hidden = false;
    }
  }
  if (!customElements.get('tb-cronometro')) customElements.define('tb-cronometro', TbCronometro);

  /* ---------- Troca de cor: mantém a posição da página e mostra carregando ---------- */
  document.addEventListener('click', (e) => {
    const cor = e.target.closest('a[data-tb-cor]');
    if (!cor || e.metaKey || e.ctrlKey || e.shiftKey || cor.classList.contains('is-atual')) return;
    try { sessionStorage.setItem('tb-rolagem', JSON.stringify({ y: window.scrollY, t: Date.now() })); } catch (err) { /* ok */ }
    cor.classList.add('is-carregando');
    document.querySelector('.tb-produto')?.classList.add('is-trocando');
  });
  try {
    const salvo = JSON.parse(sessionStorage.getItem('tb-rolagem') || 'null');
    sessionStorage.removeItem('tb-rolagem');
    if (salvo && Date.now() - salvo.t < 15000) {
      if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
      const ir = () => window.scrollTo(0, salvo.y);
      ir();
      window.addEventListener('load', ir, { once: true });
    }
  } catch (err) { /* ok */ }
  window.addEventListener('pageshow', () => {
    document.querySelectorAll('.is-carregando').forEach((el) => el.classList.remove('is-carregando'));
    document.querySelector('.tb-produto')?.classList.remove('is-trocando');
  });
  /* Coluna de informações: só fica "grudada" enquanto cabe inteira na tela.
     Se crescer (ex.: Descrição aberta), rola junto com a página normalmente. */
  const ajustarColunas = () => {
    document.querySelectorAll('.tb-produto__info').forEach((info) => {
      const cabe = info.scrollHeight + 48 <= window.innerHeight;
      info.classList.toggle('is-fixa', cabe);
    });
  };
  document.addEventListener('toggle', (e) => {
    const aba = e.target;
    if (!(aba instanceof HTMLDetailsElement) || !aba.classList.contains('tb-aba')) return;
    ajustarColunas();
    if (aba.open) {
      const titulo = aba.querySelector('summary');
      const topo = titulo.getBoundingClientRect().top;
      if (topo < 0 || topo > window.innerHeight * 0.6) window.scrollBy({ top: topo - 90, behavior: 'smooth' });
    }
  }, true);
  window.addEventListener('resize', ajustarColunas);
  window.addEventListener('load', ajustarColunas);
  if (document.readyState !== 'loading') ajustarColunas(); else document.addEventListener('DOMContentLoaded', ajustarColunas);
})();
